import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection, isDevMode } from '@angular/core';
import { RouterConfigOptions, provideRouter, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { createErrorHandler, TraceService } from '@sentry/angular';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { provideServiceWorker } from '@angular/service-worker';
import { diagnosticsInterceptor } from './core/interceptors/diagnostics.interceptor';
import { timeoutInterceptor } from './core/interceptors/timeout.interceptor';
import { networkResilienceInterceptor } from './core/interceptors/network-resilience.interceptor';
import { AppUpdateService } from './core/services/app-update.service';
import { AuthService } from './core/services/auth.service';

/**
 * BE-1556: «Назад» (свайп назад) с изменённой формой → «Остаться» возвращает браузер на запись формы
 * (`'computed'`: роутер ведёт номер записи в `history.state` и делает `history.go`). По умолчанию
 * (`'replace'`) роутер подменял адресом формы запись, куда браузер уже ушёл: второй «Назад» перепрыгивал
 * предыдущий экран, а с первого экрана приложения уходил из него без вопроса (на iPhone без `beforeunload`).
 * Уводы гвардов возвратом `UrlTree`/`RedirectCommand` (BE-1489) это не меняет — они идут заменой записи.
 */
export const ROUTER_CONFIG: RouterConfigOptions = { canceledNavigationResolution: 'computed' };

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useValue: createErrorHandler({ logErrors: !isDevMode() }) },
    TraceService,
    provideAppInitializer(() => {
      inject(AppUpdateService);
      inject(TraceService);
      // BE-804: права из /auth/me гейтят меню и роуты, а профиль лежит в localStorage
      // с момента входа — на старте перечитываем, иначе правки прав видны только
      // после перелогина.
      inject(AuthService).refreshPermissionsIfSignedIn();
    }),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withRouterConfig(ROUTER_CONFIG)),
    // Порядок важен: networkResilienceInterceptor ВНЕШНИЙ — каждая ретрай-попытка
    // GET получает свежий таймаут от timeoutInterceptor (см. интерсептор).
    provideHttpClient(withInterceptors([networkResilienceInterceptor, timeoutInterceptor, diagnosticsInterceptor, authInterceptor])),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    })
  ]
};
